import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch } from 'hooks/useStore';
import { createQuizEvent, updateQuizEvent } from 'store/quizEvent/quizEventAsync';
import IQuizEvent from 'models/QuizEvent';
import QuizEventTypes from 'types/QuizEventTypes';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Input } from 'components/Controls';
import { Button } from 'components/Buttons';
// Utilities
import { isRequired } from 'utilities/validation';

interface IForm {
  name: string;
  type: QuizEventTypes;
  quizId: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  quizId: string;
  quizEvent?: IQuizEvent;
}

const QuizEventDialog: FC<Props> = ({ open, onClose, quizId, quizEvent }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, formState: { errors } } = useForm<IForm>({
    defaultValues: {
      name: quizEvent?.name || '',
      type: quizEvent?.type || QuizEventTypes.Manual,
    }
  });

  const onSubmit = handleSubmit((data: IForm) => {
    setIsLoading(true);

    const nextData: IForm = { ...data, quizId };

    if (quizEvent) {
      dispatch(updateQuizEvent({ ...nextData, id: quizEvent._id })).unwrap()
        .then(() => onClose())
        .finally(() => setIsLoading(false));
    } else {
      dispatch(createQuizEvent(nextData)).unwrap()
        .then(() => onClose())
        .finally(() => setIsLoading(false));
    }
  });

  return (
    <Dialog size="lg" open={open} onClose={onClose}>
      <form className="flex flex-grow flex-col overflow-y-auto" onSubmit={onSubmit} noValidate>
        <DialogTitle title={quizEvent ? t('dialogs.quizEvent.updateTitle') : t('dialogs.quizEvent.createTitle')} onClose={onClose} />
        <DialogContent>
          <Controller
            control={control} name="name" rules={{ required: isRequired }}
            render={({ field }) => (
              <Input
                {...field}
                id="name"
                label={t('labels.name')}
                required={true}
                error={Boolean(errors.name)}
                errorText={errors.name ? (errors.name.message as any) : ''}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('buttons.cancel')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            type="submit"
            loading={isLoading}
          >{quizEvent ? t('buttons.save') : t('buttons.create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default QuizEventDialog;
